import styled from '@emotion/styled';
import { ButtonBase, Tooltip } from '@material-ui/core';

export const DragDropListContainer = styled('div')<{ highlight: boolean }>(
  (p) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: p.theme.spacing(2),
    marginBottom: p.theme.spacing(2),
    background: p.highlight ? p.theme.custom.colors.green.w100 : 'transparent',
    height: '100%'
  })
);

export const CONTROLS_CONTAINER_CLASS_NAME =
  'drag-drop-item-controls-container';

export const FLAGS_CONTAINER_CLASS_NAME = 'drag-drop-item-flags-container';

export const DragDropItemContainer = styled('div')<{
  selected: boolean;
  error: boolean;
  dragging: boolean;
  multiSelect?: boolean;
  disabled?: boolean;
  // NEW_UX: primary-blue selection/hover chrome (task-editor prototype), instead of the
  // classic green/yellow. The focused block reads blue even while empty; genuine errors
  // still flag red on non-selected blocks. Opt-in — classic path unchanged.
  newUx?: boolean;
  // NEW_UX: true when some (other) block is selected — while one is, hovering another
  // must NOT reveal its controls; only the selected block shows them.
  anySelected?: boolean;
}>((p) => {
  const { colors } = p.theme.custom;
  // Matches the newux kit tokens: blue600 #0061C4.
  const NEWUX_BLUE = '#0061C4';
  const NEWUX_BLUE_RING = 'rgba(0,97,196,.12)';
  const primary = p.newUx
    ? NEWUX_BLUE
    : p.multiSelect
    ? colors.yellow.w400
    : colors.green.w400;
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: 16,
    background: colors.background.light,
    borderStyle: 'solid',
    borderWidth: 2,
    borderRadius: p.theme.custom.border.radius(),
    boxShadow: p.dragging ? '0 0 8px 1px rgba(0,0,0,.15)' : 'none',
    cursor: p.multiSelect ? 'pointer' : 'inherit',
    borderColor: p.dragging
      ? p.error
        ? colors.red.w400
        : primary
      : // NEW_UX: the actively-selected block is always blue (matches the design's
      // focused state); classic keeps error-red taking precedence.
      p.newUx && p.selected
      ? NEWUX_BLUE
      : p.error
      ? p.theme.custom.colors.red.w400
      : p.selected
      ? primary
      : 'transparent',

    '> *': {
      opacity: (p.multiSelect && !p.selected) || p.disabled ? 0.3 : 1
    },

    ':hover': p.onClick
      ? {
          boxShadow: p.newUx
            ? `0 0 0 4px ${NEWUX_BLUE_RING}`
            : p.error
            ? `0 0 0 4px ${colors.red.w200}`
            : `0 0 0 4px ${
                p.multiSelect ? colors.yellow.w200 : colors.green.w200
              }`,
          borderColor: p.newUx
            ? NEWUX_BLUE
            : p.error
            ? colors.red.w400
            : p.multiSelect
            ? colors.yellow.w400
            : colors.green.w400,
          // NEW_UX: once a block is selected, hovering another must not surface its
          // controls — only the selected block shows them (via the base rule below).
          ...(p.newUx && p.anySelected
            ? {}
            : {
                [`.${CONTROLS_CONTAINER_CLASS_NAME}`]: {
                  visibility: 'visible',
                  // NEW_UX controls are self-styled white icon buttons — don't tint them.
                  ...(p.newUx
                    ? {}
                    : {
                        '> *': {
                          background: p.error
                            ? colors.red.w400
                            : p.multiSelect
                            ? colors.yellow.w400
                            : colors.green.w400
                        }
                      })
                }
              }),
          [`.${FLAGS_CONTAINER_CLASS_NAME}`]: {
            visibility: 'visible'
          },
          zIndex: 1
        }
      : {},

    [`.${CONTROLS_CONTAINER_CLASS_NAME}`]: {
      // NEW_UX: the selected block always shows its controls (they sit outside the
      // block, so hover-only would make them unreachable across the gap).
      visibility: p.newUx && p.selected ? 'visible' : 'hidden',
      opacity: 1
    },
    [`.${FLAGS_CONTAINER_CLASS_NAME}`]: {
      // visibility: p.selected ? 'visible' : 'hidden' // show on hover and when selected
      // visibility: 'hidden' // show only on hover
      visibility: 'visible'
    }
  };
});

// TODO

export const DragDropItemControlsContainer = styled('div')<{ newUx?: boolean }>(
  (p) =>
    p.newUx
      ? {
          // NEW_UX: white icon buttons stacked to the LEFT of the block (prototype).
          // The container spans from the buttons back to the block's edge (width 44,
          // buttons flush-left) so the empty span is a transparent hover-bridge — and
          // it stays visible while hovered — otherwise the mouse would cross a dead gap
          // and the controls would vanish before you reach them.
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          top: 0,
          left: -44,
          width: 44,
          alignItems: 'flex-start',
          gap: p.theme.spacing(),
          '&:hover': { visibility: 'visible' }
        }
      : {
          display: 'flex',
          position: 'absolute',
          top: '100%',
          right: -2, // border width
          color: p.theme.custom.colors.gray.white,
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: p.theme.spacing(),

          // 100% to make it easier to hover over the floating elements
          width: '100%'
        }
);

export const DragDropItemFlagsContainer = styled('div')((p) => ({
  display: 'flex',
  position: 'absolute',
  top: 0,
  right: 0,
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: p.theme.spacing(0.5)
}));

export const DragDropItemFlagContainer = styled('div')<{
  bgColor: string;
}>((p) => ({
  backgroundColor: p.bgColor,
  borderRadius: p.theme.custom.border.radius(2 / 3),
  color: p.theme.custom.colors.gray.black,
  fontSize: p.theme.custom.fontSize.xs,
  fontWeight: p.theme.typography.fontWeightBold,
  padding: `${p.theme.spacing(0.5)}px ${p.theme.spacing(0.75)}px`
}));

export const DragDropItemFlag = ({
  label,
  color,
  tooltip
}: {
  label: string;
  color: string;
  tooltip?: string;
}) => {
  return (
    <Tooltip title={tooltip || ''}>
      <DragDropItemFlagContainer bgColor={color}>
        {label}
      </DragDropItemFlagContainer>
    </Tooltip>
  );
};

export const DragDropItemControlsButton = styled(ButtonBase)<{
  newUx?: boolean;
}>((p) =>
  p.newUx
    ? {
        '&&': {
          width: 30,
          height: 30,
          minWidth: 30,
          padding: 0,
          borderRadius: 8,
          background: '#fff',
          color: '#374151',
          boxShadow: '0 1px 4px rgba(0,0,0,.12), 0 0 0 1px rgba(0,0,0,.04)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // icon-only in NEW_UX — hide the text label
          '& div': { display: 'none' }
        },
        '&&:hover': { background: '#F3F4F6', color: '#0061C4' }
      }
    : {
        '&&': {
          fontSize: p.theme.custom.fontSize.s,
          padding: p.theme.spacing(0.5),

          display: 'flex',
          alignItems: 'center',

          gap: p.theme.spacing(0.5)
        }
      }
);

const EmptyDragDropListContainer = styled('div')((p) => ({
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: p.theme.custom.colors.gray.w400,
  minHeight: 200
}));

export const DragDropListEmpty: React.FC = ({ children }) => {
  return <EmptyDragDropListContainer>{children}</EmptyDragDropListContainer>;
};
